import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import useRouter from './useRouter'
import { PlatinumData } from '../types/carbon/models/PlatinumData'
import { PlatinumProperties } from '../types/carbon/models/PlatinumProperties'
import { TableVisibilitySettings } from '../types/gotypes'
import { API_BASE_URL } from '../constants/system'
import { getDefaultTableViewSettings } from '../utils/tables'
import { extractErrorMessage } from '../utils/apitools'

export interface AutoCoderSpec {
  top: string
  side: string
  filter: string
  weight: string
  labels: Record<string, string[]>
}

const parseLabelsParam = (labelsParam: string | undefined): Record<string, string[]> => {
  if (!labelsParam) return {}
  try {
    return JSON.parse(decodeURIComponent(labelsParam))
  } catch {
    return {}
  }
}

export const useAutoCoderTable = ({
  customer,
  job,
}: {
  customer: string
  job: string
}) => {
  const router = useRouter()

  const isAutoCoderMode = router.params.storageDriver === 'autocoder'

  // Get visibility settings from router params - re-evaluated when params change
  // NOTE: We use router.params directly instead of window.location.search because
  // router.params updates synchronously when navigate() is called, while
  // window.location.search may not have updated yet when the memo recalculates.
  const visibility: TableVisibilitySettings = useMemo(() => {
    const defaults = getDefaultTableViewSettings().visibility

    // Build visibility from router params, falling back to defaults
    return {
      frequency: router.params.frequency !== undefined
        ? router.params.frequency === 'true'
        : defaults.frequency,
      column_percent: router.params.column_percent !== undefined
        ? router.params.column_percent === 'true'
        : defaults.column_percent,
      row_percent: router.params.row_percent !== undefined
        ? router.params.row_percent === 'true'
        : defaults.row_percent,
      significance: router.params.significance !== undefined
        ? router.params.significance === 'true'
        : defaults.significance,
    }
  }, [
    router.params.frequency,
    router.params.column_percent,
    router.params.row_percent,
    router.params.significance
  ])

  // Build displayProperties from visibility settings
  // This is the new preferred format that matches PlatinumProperties in Carbon
  const displayProperties: PlatinumProperties = useMemo(() => {
    return {
      cells: {
        frequencies: visibility.frequency,
        columnPercents: visibility.column_percent,
        rowPercents: visibility.row_percent,
        percentSign: false,
        missingAsZero: false,
        missingAsBlank: false,
        zeroAsBlank: false,
        blankAsChar: false,
        percentsAsProportions: false,
        thousandsSeparator: false,
        showRedundant100: false,
      },
      decimals: {
        // Fixed decimal places for AutoCoder tables
        percents: 1,      // 1 decimal place for percentages (e.g., "20.0%")
        frequencies: 0,   // No decimal places for frequencies
        statistics: 1,    // 1 decimal place for statistics
        expressions: 2,   // 2 decimal places for expressions
      },
      significance: {
        visible: visibility.significance,
        type: 'ColumnGroups',
        propStat: 'Z',
        meanStat: 'T',
        autoParam: false,
        letters: 'Column',
        appendLetters: false,
        headcount: 'Weighted',
        meanPoolVariance: false,
        propPooledEst: false,
        continuityCorr: false,
        skipBase30: false,
        skipCell5: false,
        lettersLong: false,
        oneTailed: false,
        hiLoTest: false,
      },
    }
  }, [visibility])

  const spec: AutoCoderSpec | null = isAutoCoderMode ? {
    top: router.params.acTop || '',
    side: router.params.acSide || '',
    filter: router.params.acFilter || '',
    weight: router.params.acWeight || '',
    labels: parseLabelsParam(router.params.acLabels),
  } : null

  // Include displayProperties in query key so changes trigger re-fetch
  const query = useQuery({
    queryKey: [
      'autocoder-table',
      customer,
      job,
      spec?.top,
      spec?.side,
      spec?.filter,
      spec?.weight,
      JSON.stringify(spec?.labels),
      // Display properties in query key - changes trigger re-fetch
      JSON.stringify(displayProperties),
    ],
    queryFn: async () => {
      if (!spec || !customer || !job) return null
      try {
        const response = await axios.post<PlatinumData>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/spec/run`,
          {
            top: spec.top,
            side: spec.side,
            filter: spec.filter,
            weight: spec.weight,
            labels: spec.labels,
            displayProperties: displayProperties,  // Pass displayProperties to backend (new format)
          }
        )
        return response.data
      } catch (error) {
        // Carbon's reason for refusing the spec, not axios's status line
        throw new Error(extractErrorMessage(error))
      }
    },
    // A refused spec is refused again. Retrying kept the page on its spinner.
    retry: false,
    enabled: isAutoCoderMode && !!customer && !!job,
  })

  return {
    isAutoCoderMode,
    spec,
    visibility,
    data: query.data || undefined,
    loading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
  }
}

export default useAutoCoderTable
